import useAuth from "@/hooks/stores/useAuth";
import { fieldConditionsService, userService, authService, fileUploadService, formDataService } from "@/services";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast, Toaster } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import UnsavedChangesAlert from "./UnsavedChangesAlert";
import videoPoster from '../../../assets/images/image.png';
import { InitialQuestionnaireDisplay } from "@/components/InitialQuestionnaireDisplay";
import SkippableInput from "@/components/SkippableInput";
import { useDropzone } from "react-dropzone";
import FileUploadDropzone from "@/pages/Users/Dynamic_Form_parent/FileUploadDropzone";
import { useMobileMenu } from "@/components/Navbar/Navbar";
import { settingsService } from "@/services/settingsService";
import { debounce } from 'lodash';
import FormSidebar from '@/pages/Users/Dynamic_Form_parent/FormSidebar';
import PostScryptContacts from '@/pages/Users/Dynamic_Form_parent/PostScryptContacts';
import ChangingWill from '@/pages/Users/Dynamic_Form_parent/ChangingWill';
import BreakSuggestionModal from "@/components/BreakSuggestionModal";
import FormCompletionModal from "@/components/FormCompletionModal";
import { Info } from "lucide-react";
import Footer from "@/components/Footer";
import { VIDEO_URLS } from '../../../config/constants';

// Add this debounced save function outside the component
const debouncedSaveStepData = debounce((getLatestData, callback) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await getLatestData();
      if (callback) callback(result);
      resolve(result);
    } catch (error) {
      console.error('Error in debounced save:', error);
      reject(error);
    }
  });
}, 1000);

// Add this helper function near the top of the file, after the imports
const getCurrentTime = () => {
  return new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};



const DynamicForm = ({ formData }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState("form");
  const [formValues, setFormValues] = useState({});
  const formValuesRef = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [formName, setFormName] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastFocusedField, setLastFocusedField] = useState(null);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(() => {
    // Initialize from localStorage on component mount
    const loginResponse = localStorage.getItem('loginResponse');
    if (!loginResponse) return false;
    
    const parsedResponse = JSON.parse(loginResponse);
    return parsedResponse.user?.is_new === 1;
  });
  const [newUserApiResponse, setNewUserApiResponse] = useState(null);
  const user_id = 2;
  const [fieldConditions, setFieldConditions] = useState([]);
  const [hiddenFields, setHiddenFields] = useState({});
  const { userId } = useAuth();
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const mainContentRef = useRef(null);
  const [completedSteps, setCompletedSteps] = useState(() => {
    // Try to load completed steps from localStorage with user-specific key
    const savedSteps = localStorage.getItem(`user_${userId}_form_${formData?.id}_completed_steps`);
    if (savedSteps) {
      try {
        // Parse the saved steps and convert to a Set
        const parsedSteps = JSON.parse(savedSteps);
        return new Set(parsedSteps);
      } catch (e) {
        console.error("Error parsing saved steps:", e);
        return new Set();
      }
    }
    return new Set();
  });
  const [userName, setUserName] = useState("");
  // Add state to track newly added sections
  const [newlyAddedSection, setNewlyAddedSection] = useState(null);
  const { isOpen, close } = useMobileMenu();

  // Add these new state variables
  const [typingTimeouts, setTypingTimeouts] = useState({});
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(5000);
  
  const [lastBreakTime, setLastBreakTime] = useState(Date.now());
  const [isFirstVisit, setIsFirstVisit] = useState(true);

  //Hmnsd
  // Add validation error state
  const [validationErrors, setValidationErrors] = useState({});

  // Add a simple toast throttle to prevent multiple notificationsddfd
  let lastToastTime = 0;
  const toastThrottleTime = 2000; // 2 seconds between toasts

  // Add a ref to track if a save is in progress
  const saveInProgress = useRef(false);

  // Add this effect to save completedSteps to localStorage whenever it changes
  useEffect(() => {
    if (formData?.id && userId) {
      // Convert Set to Array for JSON serialization
      const stepsArray = Array.from(completedSteps);
      localStorage.setItem(`user_${userId}_form_${formData.id}_completed_steps`, JSON.stringify(stepsArray));
      console.log("Saved completed steps to localStorage for user:", userId, stepsArray);
    }
  }, [completedSteps, formData?.id, userId]);

  // Log form data
  useEffect(() => {
    console.log("Form Data:", formData);
  }, [formData]);

  useEffect(() => {
    const fetchFieldConditions = async () => {
      try {
        const response = await fieldConditionsService.getFieldConditions();
        console.log("Raw Response: ", response);

        // If response is already JSON, no need to parse it
        const data =
          typeof response.json === "function"
            ? await response.json()
            : response;
        console.log("Data: ", data);

        if (Array.isArray(data.data)) {
          setFieldConditions(data.data);
        } else {
          console.error("Unexpected data format:", data);
        }
      } catch (error) {
        console.error("Error fetching field conditions:", error);
      }
    };

    fetchFieldConditions();
  }, []); // Only run once on component mount

  // Update the formValues effect to use the stored fieldConditions
  useEffect(() => {
    if (!formData?.form_form_steps || fieldConditions.length === 0) return;

    formData.form_form_steps.forEach((step) => {
      step.form_step_form_sections.forEach((section) => {
        console.log("section id", section);
        const sectionValues = formValues[section.id] || [];
        sectionValues.forEach((rowValues, index) => {
          section.form_step_form_fields.forEach((field) => {
            const currentValue = rowValues[field.id];
          
            const conditions = fieldConditions.filter(
              (cond) => cond.field_id == field.id
            );
            
            conditions.forEach((condition) => {
              formData.form_form_steps.forEach((step) => {
                step.form_step_form_sections.forEach((section) => {
                  section.form_step_form_fields.forEach((section_field) => {
                    if (section_field.id == condition.condition_field_id) {
                      if (currentValue !== undefined && currentValue !== '') {
                        if (condition.oparator == "=" && currentValue == condition.value) {
                          // console.log("marking true", section_field.id);
                          section_field.hidden = true;
                        } else {
                          // console.log("marking true", section_field.id);
                          section_field.hidden = false;
                        }
                      } else {
                        section_field.hidden = true;
                      }
                    }
                  });
                });
              });
            });
          });
        });
      });
    });
  }, [formData, formValues, fieldConditions]);

  // Combine the two useEffect hooks into one to avoid race conditions
  useEffect(() => {
    if (!formData?.form_form_steps) return;

    const initializeFormData = async () => {
      setIsLoading(true);
      try {
        // First initialize with default values
        const newFormValues = {};
        formData.form_form_steps.forEach((step) => {
          step.form_step_form_sections.forEach((section) => {
            const repeatCount = Math.max(
              parseInt(section.default_repeat_count) || 1,
              1
            );
            let rowValues = [];

            for (let rowIndex = 0; rowIndex < repeatCount; rowIndex++) {
              let rowObj = {};
              section.form_step_form_fields.forEach((field) => {
                const defaultValue = field.form_section_defaults?.find(
                  (def) =>
                    def.row_index === rowIndex && def.form_field_id === field.id
                );
                rowObj[field.id] = defaultValue
                  ? defaultValue.default_value
                  : "";

               
                if (section.id === 18) {
                   if (field.id === 77) {
                    console.log('field id 77')
                    rowObj[field.id] = getCurrentUserName(); // User's name
                  }
                  if (field.id === 78) {
                    console.log('field id 78')
                    rowObj[field.id] = new Date().toISOString().split('T')[0]; // Today's date
                  }
                 
                }
              });
              rowValues.push(rowObj);
            }
            newFormValues[section.id] = rowValues;
          });
        });

        // Then fetch and merge user submission data
        const responseJson = await formDataService.getUserData(userId);

        // Add logging for the entire backend response
        console.log("Backend API Response:", responseJson);

        if (responseJson.success && responseJson.data.form_submissions?.[0]) {
          setFormName(responseJson.data.form_submissions[0].form.name);
          const submissionValues =
            responseJson.data.form_submissions[0].submission_values;
          
          // Log all submission values from backend
          console.log("Submission values from backend:", submissionValues);

          // Group submission values by section and row index
          const valuesBySection = {};
          submissionValues.forEach((submission) => {
            const sectionId = submission.section;
            const rowIndex = submission.section_repeat_index ?? 0;
            if (!valuesBySection[sectionId]) {
              valuesBySection[sectionId] = {};
            }
            if (!valuesBySection[sectionId][rowIndex]) {
              valuesBySection[sectionId][rowIndex] = {};
            }
            valuesBySection[sectionId][rowIndex][submission.field_id] =
              submission.value;
          });

          // Log the grouped values by section
          console.log("Grouped values by section:", valuesBySection);

          // Merge submission values with default values
          formData.form_form_steps.forEach((step) => {
            step.form_step_form_sections.forEach((section) => {
              let repeatCount = Math.max(
                parseInt(section.default_repeat_count) || 1,
                1
              );
              if (valuesBySection[section.id]) {
                repeatCount = Math.max(
                  repeatCount,
                  Object.keys(valuesBySection[section.id]).length
                );
              }

              const rowData = newFormValues[section.id] || [];

              for (let rowIndex = 0; rowIndex < repeatCount; rowIndex++) {
                const rowInstance = rowData[rowIndex] || {};
                const rowSubmission =
                  valuesBySection[section.id]?.[rowIndex] || {};

                section.form_step_form_fields.forEach((field) => {
                  if (rowSubmission.hasOwnProperty(field.id)) {
                    // Special handling for file fields
                    if (field.field_type === "file") {
                      console.log(`Processing file field ${field.id} from backend:`, rowSubmission[field.id]);
                      
                      // Backend might send file data in different formats
                      let fileValue = rowSubmission[field.id];
                      
                      try {
                        // If fileValue is a string, handle empty strings specifically
                        if (typeof fileValue === 'string') {
                          // Handle empty string case explicitly
                          if (fileValue === '') {
                            console.log("Empty file value received from backend");
                            rowInstance[field.id] = null;
                          } else {
                            try {
                              // Try to parse as JSON first
                              fileValue = JSON.parse(fileValue);
                              console.log("Successfully parsed JSON file data:", fileValue);
                              
                              // Use the parsed object directly
                              rowInstance[field.id] = {
                                ...fileValue,
                                from_backend: true
                              };
                            } catch (e) {
                              // If JSON parsing fails, treat it as a regular file path
                              console.log("Treating file value as path:", fileValue);
                              rowInstance[field.id] = {
                                name: fileValue.includes('/') ? fileValue.split('/').pop() : `File ${field.id}`,
                                url: fileValue.includes('/') ? fileValue : `https://api.postscrypt.co.uk/files/${fileValue}`,
                                file_id: fileValue.includes('/') ? fileValue.split('/').pop().split('_')[0] : fileValue,
                                submission_id: fileValue.includes('/') ? fileValue.split('/').pop().split('_')[0] : fileValue,
                                from_backend: true
                              };
                            }
                          }
                        } 
                        // If it's already an object, use it directly
                        else if (typeof fileValue === 'object' && fileValue) {
                          rowInstance[field.id] = {
                            ...fileValue,
                            from_backend: true
                          };
                        }
                        
                        console.log("Final processed file object:", rowInstance[field.id]);
                      } catch (error) {
                        console.error("Error processing file value:", error);
                        rowInstance[field.id] = null;
                      }
                    } else {
                      // For non-file fields, just copy the value
                      rowInstance[field.id] = rowSubmission[field.id];
                    }
                  }
                });
                rowData[rowIndex] = rowInstance;
              }
              newFormValues[section.id] = rowData;
            });
          });
        }

        setFormValues(newFormValues);

        // console.log(newFormValues);
        // console.log(formData.form_form_steps);

        formData.form_form_steps
          .flatMap((step) =>
            step.form_step_form_sections.flatMap((section) =>
              section.form_step_form_fields.map((field) => ({
                field: field,
                sectionId: section.id,
              }))
            )
          )
          .forEach(({ field, sectionId }) => {
            const value = newFormValues[sectionId]?.find(
              (fieldSet) => fieldSet[field.id] !== undefined
            )?.[field.id];
            if (value !== undefined) {
              conditionalCheck(field, value);
            }
          });
      } catch (error) {
        console.error("Error initializing form data:", error);
      } finally {
        // console.log("Form data initialized");
        // alert("Form data initialized");
        setIsLoading(false);
        // TODO: If user has no submissions then redirect to introduction page
      }
    };

    initializeFormData();
  }, [formData]);

  // Fetch field conditions from the API once

  // Add this debug function
  const debugFieldConditions = () => {
    console.log("Current field conditions:", fieldConditions);
    console.log("Current form values:", formValues);
    
    // Log all fields with their hidden status
    formData.form_form_steps.forEach((step) => {
      step.form_step_form_sections.forEach((section) => {
        section.form_step_form_fields.forEach((field) => {
          console.log(`Field ${field.id} (${field.name}) hidden: ${field.hidden}`);
        });
      });
    });
  };

  // Update the conditionalCheck function to be more explicit
  const conditionalCheck = (field, value) => {
    const conditions = fieldConditions.filter(
      (cond) => cond.field_id == field.id
    );
    if (conditions.length > 0) {
      console.log("Conditions: ", conditions);
    }

    conditions.forEach((condition) => {
      formData.form_form_steps.forEach((step) => {
        step.form_step_form_sections.forEach((section) => {
          section.form_step_form_fields.forEach((section_field) => {
            if (section_field.id == condition.condition_field_id) {
              // Only apply condition if value is defined and not empty
              if (value !== undefined && value !== '') {
                if (condition.oparator == "=" && value == condition.value) {
                  console.log("marking true", section_field.id);
                  section_field.hidden = true;
                } else {
                  section_field.hidden = false;
                }
              } else {
                // If value is undefined or empty, show the field by default
                section_field.hidden = true;
              }
            }
          });
        });
      });
    });
  };

  // Add a useEffect to initialize specific fields as hidden by default
  useEffect(() => {
    if (!formData?.form_form_steps) return;
    
    // Initialize all fields as visible by default
    formData.form_form_steps.forEach((step) => {
      step.form_step_form_sections.forEach((section) => {
        section.form_step_form_fields.forEach((field) => {
          // Hide file upload fields 53 and 55 by default
          if (field.id === 53 || field.id === 55) {
            field.hidden = true;
          } else {
            field.hidden = false;
          }
          
          // Also hide textarea fields 56 and 57 by default
          if (field.id === 56 || field.id === 57) {
            field.hidden = true;
          }
        });
      });
    });
    
    console.log("Fields 53, 55, 56, and 57 initialized as hidden by default");
  }, [formData]);

  // Function to check and update the visibility of fields
  const updateFieldVisibility = (formValues) => {
    const echPlanFieldId = 52; // ID for "Is there an education, Health and Care(ECH) plan in place?"
    const carePlanFieldId = 54; // ID for "Is there a care / support plan in place?"

    const echPlanValue = formValues[12]?.[0]?.[echPlanFieldId];
    const carePlanValue = formValues[12]?.[0]?.[carePlanFieldId];

    console.log("ECH Plan Value:", echPlanValue);
    console.log("Care Plan Value:", carePlanValue);

    formData.form_form_steps.forEach((step) => {
      step.form_step_form_sections.forEach((section) => {
        section.form_step_form_fields.forEach((field) => {
          // Show field 53 (Upload ECH plan) only if field 52 is "yes"
          if (field.id === 53) {
            field.hidden = echPlanValue !== 'yes';
          }
          
          // Show field 55 (Upload care/support plan) only if field 54 is "yes"
          if (field.id === 55) {
            field.hidden = carePlanValue !== 'yes';
          }
          
          // Show fields 56 and 57 only if both radio buttons are "no"
          if (field.id === 56 || field.id === 57) {
            const shouldShow = echPlanValue === 'no' && carePlanValue === 'no';
            field.hidden = !shouldShow;
            console.log(`Field ${field.id} hidden:`, field.hidden);
          }
        });
      });
    });
  };

  // Add this useEffect to fetch the auto-save timeout from settings
  useEffect(() => {
    const initializeAutoSave = async () => {
      try {
        const timeout = await settingsService.getAutoSaveTimeout();
        setAutoSaveTimeout(timeout);
        console.log("Auto-save timeout set to:", timeout);
      } catch (error) {
        console.error("Error fetching auto-save timeout:", error);
        // Keep default of 7000ms if fetch fails
      }
    };

    initializeAutoSave();
  }, []);

  // Update validateField function to include validation for fields 76 and 79
  const validateField = (sectionId, field, value, index = 0) => {
    // Only validate fields in the My Messages section (section ID 18)
    if (sectionId !== 18) return null;
    
    // Skip validation for auto-filled fields (77 - name, 78 - date)
    if (field.id === 77 || field.id === 78) return null;
    
    // Check if field is required and empty (includes fields 76 and 79)
    if (field.required === 1 || field.id === 76 || field.id === 79) {
      const isEmpty = 
        value === undefined || 
        value === null || 
        value === "" || 
        (typeof value === "string" && value.trim() === "") ||
        (field.field_type === "checkbox" && value !== "1") ||
        (field.field_type === "radio" && !value) ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && Object.keys(value).length === 0);
        
      if (isEmpty) {
        if (field.id === 76) {
          return "Please fill in the required field";
        } else if (field.id === 79) {
          return "Please fill in the required field";
        }
        return "This field is required";
      }
    }
    
    return null;
  };

  // Update handleChange to clear validation errors and toast when content is added to field 79
  const handleChange = (sectionId, field, value, index = 0) => {
    // Skip state updates for auto-filled fields in section 18
    if (sectionId === 18 && (field.id === 77 || field.id === 78)) {
      return;
    }

    // Clear validation error for this specific field when user starts typing
    if (validationErrors[`${sectionId}_${field.id}_${index}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${sectionId}_${field.id}_${index}`];
        return newErrors;
      });
    }

    setFormValues((prevValues) => {
      const sectionValues = prevValues[sectionId] || [];
      const updatedSectionValues = [...sectionValues];

      if (!updatedSectionValues[index]) {
        updatedSectionValues[index] = {};
      }

      updatedSectionValues[index] = {
        ...updatedSectionValues[index],
        [field.id]: value,
      };

      // For section 18, ensure fields 77 and 78 maintain their values
      if (sectionId === 18) {
        updatedSectionValues[index][77] = getCurrentUserName();
        updatedSectionValues[index][78] = new Date().toISOString().split('T')[0];
      }

      // If this is a file upload field in section 12, update the corresponding radio button
      if (sectionId === 12 && field.field_type === "file") {
        // For field 53 (ECH plan upload), set field 52 to "yes"
        if (field.id === 53 && value && Object.keys(value).length > 0) {
          updatedSectionValues[index][52] = "yes";
        }
        
        // For field 55 (care plan upload), set field 54 to "yes"
        if (field.id === 55 && value && Object.keys(value).length > 0) {
          updatedSectionValues[index][54] = "yes";
        }

        // If it's a file upload, trigger immediate save
        const newFormValues = {
          ...prevValues,
          [sectionId]: updatedSectionValues,
        };

        // Update the ref with the latest values
        formValuesRef.current = newFormValues;

        // Use setTimeout to ensure state is updated before saving
        setTimeout(() => {
          saveFileData(newFormValues, sectionId, field.id, index, value);
        }, 0);
      }
    //chls
      console.log(`Field ${field.id} changed to:`, value);
      conditionalCheck(field, value);
      setHasUnsavedChanges(true);

      const newFormValues = {
        ...prevValues,
        [sectionId]: updatedSectionValues,
      };
      
      // Update the ref with the latest values
      formValuesRef.current = newFormValues;
      
      updateFieldVisibility(newFormValues);

      return newFormValues;
    });

    setHasUnsavedChanges(true);

    // Clear existing timeout for this field
    if (typingTimeouts[`${sectionId}_${field.id}_${index}`]) {
      clearTimeout(typingTimeouts[`${sectionId}_${field.id}_${index}`]);
    }

    // Set a new timeout for this field
    const timeout = setTimeout(() => {
      // Use a function to get the latest form state from the ref
      const getLatestData = async () => {
        return saveStepData(currentStep, formValuesRef.current);
      };
      
      debouncedSaveStepData(getLatestData)
        .then((result) => {
          if (result === true) {
            const time = new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            setLastSaveTime(time);
            // Ensure hasUnsavedChanges is set to false synchronously
            setHasUnsavedChanges(false);
          }
        })
        .catch(error => {
          console.error("Error in auto-save:", error);
        });
      
      setTypingTimeouts(prev => {
        const newTimeouts = {...prev};
        delete newTimeouts[`${sectionId}_${field.id}_${index}`];
        return newTimeouts;
      });
    }, autoSaveTimeout);
    
    setTypingTimeouts(prev => ({
      ...prev,
      [`${sectionId}_${field.id}_${index}`]: timeout
    }));
  };

  // Update the saveFileData function
  const saveFileData = async (formValues, sectionId, fieldId, index, fileValue) => {
    try {
      setIsSaving(true);
      
      // Handle empty string case explicitly
      const fileStringValue = fileValue === "" ? "" : (
        typeof fileValue === 'string' 
          ? fileValue 
          : (fileValue.url || fileValue.file_id || fileValue.submission_id)
      );

      // Prepare payload for file upload
      const payload = {
        form_id: Number(formData.id),
        step_id: Number(formData.form_form_steps[currentStep].id),
        user_id: Number(userId),
        values: [
          {
            field_id: Number(fieldId),
            section: Number(sectionId),
            section_repeat_index: Number(index),
            value: fileStringValue
          }
        ]
      };

      // Add corresponding radio button value if needed
      if (sectionId === 12) {
        if (fieldId === 53) {
          payload.values.push({
            field_id: 52,
            section: Number(sectionId),
            section_repeat_index: Number(index),
            value: fileStringValue === "" ? "no" : "yes"
          });
        } else if (fieldId === 55) {
          payload.values.push({
            field_id: 54,
            section: Number(sectionId),
            section_repeat_index: Number(index),
            value: fileStringValue === "" ? "no" : "yes"
          });
        }
      }

      console.log("Saving file data payload:", payload);

      const result = await formDataService.saveFileData(payload);
      
      if (result.status === "success") {
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        setHasUnsavedChanges(false);
        
        console.log("File data saved successfully:", result);

        // Update form state with the string URL or empty string
        setFormValues(prevValues => {
          const updatedValues = { ...prevValues };
          if (!updatedValues[sectionId]) {
            updatedValues[sectionId] = [];
          }
          if (!updatedValues[sectionId][index]) {
            updatedValues[sectionId][index] = {};
          }
          updatedValues[sectionId][index][fieldId] = fileStringValue;
          return updatedValues;
        });
      } else {
        throw new Error(result.message || 'Failed to save file data');
      }
    } catch (error) {
      console.error("Exception saving file data:", error);
      setErrorMessage("Error saving file data. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Add a cleanup effect for the timeouts
  useEffect(() => {
    return () => {
      // Clean up all timeouts when component unmounts
      Object.values(typingTimeouts).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [typingTimeouts]);

  // Update handleFieldBlur to use the same time format consistently
  const handleFieldBlur = async (field, value) => {
    // Skip validation and save if the field is being handled by auto-save
    if (typingTimeouts[`${field.sectionId}_${field.id}_0`]) {
      return;
    }

    // Only save if there's a real value to save
    if (value !== undefined && value !== null && value !== '') {
      try {
        await saveStepData(currentStep, formValuesRef.current);
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error saving field:", error);
      }
    }
  };

  // Update the addRepeatableSection function
  const addRepeatableSection = (sectionId) => {
    setFormValues((prevValues) => {
      const section = formData.form_form_steps
        .flatMap((step) => step.form_step_form_sections)
        .find((section) => section.id === sectionId);

      if (!section) return prevValues;

      const sectionValues = prevValues[sectionId] || [];
      const newRowIndex = sectionValues.length;
      const newRow = {};

      section.form_step_form_fields.forEach((field) => {
        const defaultValue = field.form_section_defaults?.find(
          (def) => def.row_index === newRowIndex
        );
        newRow[field.id] = defaultValue ? defaultValue.default_value : "";
      });

      // Add the new row at the beginning of the array instead of the end
      const newFormValues = {
        ...prevValues,
        [sectionId]: [newRow, ...sectionValues],
      };
      
      // Update ref with latest values
      formValuesRef.current = newFormValues;
      
      return newFormValues;
    });
    
    // Set the newly added section ID and index
    setNewlyAddedSection({
      sectionId,
      index: 0,
      timestamp: Date.now()
    });

    // Focus the first input field in the new section
    setTimeout(() => {
      const firstInput = document.querySelector(`[data-section-id="${sectionId}"] input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), [data-section-id="${sectionId}"] textarea`);
      if (firstInput) {
        firstInput.focus();
        firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Clear the highlight after 3 seconds
    setTimeout(() => {
      setNewlyAddedSection(null);
    }, 3000);
  };

  // Remove a row from a repeatable section while enforcing a minimum row count
  const removeRepeatableSection = async (sectionId, index) => {
    // First check if we can remove this row
    const section = formData.form_form_steps
      .flatMap((step) => step.form_step_form_sections)
      .find((section) => section.id === sectionId);

    if (!section) return;

    const minRows =
      section.is_repeatable === "Yes"
        ? parseInt(section.default_repeat_count) || 1
        : 1;

    const sectionValues = formValues[sectionId] || [];

    // Prevent removing rows if we are at the minimum required
    if (sectionValues.length <= minRows) {
      // console.log(`Cannot remove row: minimum ${minRows} rows required`);
      return;
    }

    // Update the form values
    setFormValues((prevValues) => {
      // Append removed section details to a "remove" key
      const removedSection = sectionValues[index];
      const updatedSectionValues = sectionValues.filter((_, i) => i !== index);

      const updatedFormValues = {
        ...prevValues,
        [sectionId]: updatedSectionValues,
      };

      if (!updatedFormValues.remove) {
        updatedFormValues.remove = [];
      }

      updatedFormValues.remove.push({
        section_id: sectionId,
        section_repeat_index: index,
        fields: removedSection,
      });

      // Update ref with latest values
      formValuesRef.current = updatedFormValues;
      
      return updatedFormValues;
    });

    // Set unsaved changes flag
    setHasUnsavedChanges(true);

    // Save the data immediately after removing the section
    try {
      setIsSaving(true);
      await saveStepData(currentStep, formValuesRef.current);
      
      // Update last save time on successful save
      const time = new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      setLastSaveTime(time);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving after section removal:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Add a useEffect to keep formValuesRef in sync with formValues
  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  // Save the current step's data to the backend (partial save)
  const saveStepData = async (stepIndex, formValuesOverride = null) => {
    const step = formData.form_form_steps[stepIndex];
    const payload = {
      form_id: formData.id,
      step_id: step.id,
      user_id: userId,
      values: [],
      removed_sections: (formValuesOverride || formValues).remove || [],
    };

    // Check if we have a Messages section
    const hasMessagesSection = step.form_step_form_sections.some(section => section.id === 18);
    
    // Only do validation checking when we have a Messages section
    let hasEmptyRequiredFields = false;
    
    if (hasMessagesSection) {
      // Check fields 76 and 79 only once
      const valuesToCheck = formValuesOverride || formValues;
      const subject = valuesToCheck[18]?.[0]?.[76];
      const content = valuesToCheck[18]?.[0]?.[79];
      
      hasEmptyRequiredFields = !subject || subject.trim() === '' || !content || content.trim() === '';
    }

    // Build payload without modifying validation errors
    step.form_step_form_sections.forEach((section) => {
      const valuesToUse = formValuesOverride || formValues;
      const repeatedRows = valuesToUse[section.id] || [];
      repeatedRows.forEach((rowData, sectionRepeatIndex) => {
        Object.keys(rowData).forEach((fieldId) => {
          const value = rowData[fieldId];
          // Add logging for file fields
          const field = section.form_step_form_fields.find(f => f.id == fieldId);
          if (field && field.field_type === "file" && value) {
            console.log(`Saving file field ${fieldId} for section ${section.id}:`, {
              value,
              type: typeof value,
              hasUrl: value.url ? 'yes' : 'no',
              hasId: value.file_id || value.submission_id ? 'yes' : 'no'
            });
            
            // For section 12, field 53 (ECH plan) and field 55 (support plan), ensure we're sending the right data
            if ((section.id === 12 && (fieldId == 53 || fieldId == 55))) {
              console.log("Special file field detected:", {
                sectionId: section.id,
                fieldId: fieldId,
                value
              });
            }
          }
          
          payload.values.push({
            field_id: fieldId,
            section: section.id,
            section_repeat_index: sectionRepeatIndex,
            value,
          });
        });
      });
    });

    // Only show validation toast if this is a save attempt (not auto-save)
    if (hasMessagesSection && hasEmptyRequiredFields && saveInProgress.current) {
      // Dismiss any existing toasts before showing new one
      toast.dismiss();
      toast.error("Please fill out all required fields", {
        style: {
          background: '#F16745',
          color: '#fff',
          border: 'none'
        }
      });
      return false;
    }

    try {
      const result = await formDataService.saveStepData(payload);

      if (result.status === "success") {
        // Success - clear any validation errors for section 18
        if (hasMessagesSection) {
          // Clear all toast notifications before showing success
          toast.dismiss();
        }
        
        // Update last save time on successful save
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        
        // Set hasUnsavedChanges to false on successful save
        setHasUnsavedChanges(false);
        
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("Exception saving step data:", err);
      return false;
    }
  };

  // Updated isStepComplete function
  const isStepComplete = (stepIndex) => {
    const step = formData.form_form_steps[stepIndex];
    if (!step) return false;

    let isComplete = true;

    step.form_step_form_sections.forEach((section) => {
      const sectionValues = formValues[section.id] || [];

      // If there are no values for this section and it's required, step is incomplete
      if (sectionValues.length === 0) {
        const hasRequiredFields = section.form_step_form_fields.some(
          (field) => field.required === 1
        );
        if (hasRequiredFields) {
          isComplete = false;
          return;
        }
      }

      // Check each row in the section
      sectionValues.forEach((rowInstance) => {
        section.form_step_form_fields.forEach((field) => {
          // Skip if field is hidden
          if (field.hidden) return;

          // Only check required fields
          if (field.required === 1) {
            const value = rowInstance[field.id];
            // Check if the value is empty (empty string, undefined, null) or only whitespace
            if (
              value === undefined ||
              value === null ||
              (typeof value === "string" && value.trim() === "") ||
              (field.field_type === "checkbox" && value !== "1") ||
              (field.field_type === "radio" && !value)
            ) {
              isComplete = false;
            }
          }
        });
      });
    });

    return isComplete;
  };

  // Handle moving to the next step
  const handleNext = async () => {
    if (hasUnsavedChanges) {
      const userConfirmed = window.confirm(
        "Moving to the next step will not save your changes. Do you want to proceed?"
      );
      if (!userConfirmed) {
        return; // If the user cancels, do nothing
      }
    }
    //console.log("Cuurrent State",currentStep);
    //console.log("Current Step",currentStep);
    setCurrentStep((prev) => prev + 1);
  };

  // Update the scrollToTop function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Replace the areAllFieldsFilled function with this updated version
  const areAllFieldsFilled = (stepIndex) => {
    const step = formData.form_form_steps[stepIndex];
    if (!step) return false;

    // Check if any textarea fields are empty
    let allTextareasHaveContent = true;
    
    for (const section of step.form_step_form_sections) {
      const sectionValues = formValues[section.id] || [];
      
      // Check each row in the section
      for (const rowInstance of sectionValues) {
        for (const field of section.form_step_form_fields) {
          // Skip if field is hidden
          if (field.hidden) continue;
          
          // For textarea fields, we want content even if not required
          if (field.field_type === "textarea") {
            const value = rowInstance[field.id];
            
            // Check if textarea is empty
            if (value === undefined || value === null || value === "" || (typeof value === "string" && value.trim() === "")) {
              console.log(`Empty textarea field: ${field.name}, Value: "${value}"`);
              allTextareasHaveContent = false;
            }
          }
          
          // For required fields of any type
          if (field.required === 1) {
            const value = rowInstance[field.id];
            
            // Check if required field is empty
            const isEmpty =
              value === undefined ||
              value === null ||
              value === "" ||
              (typeof value === "string" && value.trim() === "") ||
              (field.field_type === "checkbox" && value !== "1") ||
              (field.field_type === "radio" && !value) ||
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === "object" && Object.keys(value).length === 0);
              
            if (isEmpty) {
              console.log(`Empty required field: ${field.name}, Value: "${value}"`);
              return false;
            }
          }
        }
      }
    }
    
    // Only return true if all required fields are filled AND all textareas have content
    return allTextareasHaveContent;
  };

  // Function to validate all fields in the current step and section
  const validateStep = (stepIndex) => {
    const step = formData.form_form_steps[stepIndex];
    if (!step) return false;
    
    let newErrors = {};
    let isValid = true;
    
    // For "My Messages" section specifically
    const messagesSection = step.form_step_form_sections.find(section => section.id === 18);
    if (messagesSection) {
      const sectionValues = formValues[18] || [];
      
      // Check each row in the section
      sectionValues.forEach((rowInstance, rowIndex) => {
        messagesSection.form_step_form_fields.forEach((field) => {
          // Skip if field is hidden
          if (field.hidden) return;
          
          // Don't validate auto-filled fields (name and date)
          if (field.id === 77 || field.id === 78) return;
          
          const value = rowInstance[field.id];
          const error = validateField(18, field, value, rowIndex);
          
          if (error) {
            newErrors[`18_${field.id}_${rowIndex}`] = error;
            isValid = false;
          }
        });
      });
    }
    
    setValidationErrors(newErrors);
    return isValid;
  };

  // Update handleSaveAndNext to include first visit logic
  const handleSaveAndNext = async () => {
    try {
      // Dismiss any existing toasts first
      toast.dismiss();
      
      // Validate the current step first
      const isStepValid = validateStep(currentStep);
      
      // Special handling for the My Messages section
      const hasMessagesSection = formData.form_form_steps[currentStep].form_step_form_sections.some(
        section => section.id === 18
      );
      
      if (hasMessagesSection && !isStepValid) {
        toast.error("Please fill out all required fields in the My Messages section", {
          style: {
            background: '#F16745',
            color: '#fff',
            border: 'none'
          }
        });
        setIsSaving(false);
        return;
      }
      
      setIsSaving(true);
      // Use the latest values from the ref
      await saveStepData(currentStep, formValuesRef.current);

      // Check if all fields are filled according to our criteria
      const allFieldsFilled = areAllFieldsFilled(currentStep);
      console.log("Are all fields filled:", allFieldsFilled);

      // Update completedSteps based on field completion
      if (allFieldsFilled) {
        console.log(`Step ${currentStep} marked as complete`);
        setCompletedSteps(prev => {
          const newSet = new Set([...prev]);
          newSet.add(currentStep);
          return newSet;
        });
      } else {
        console.log(`Step ${currentStep} marked as incomplete`);
        setCompletedSteps(prev => {
          const newSet = new Set([...prev]);
          newSet.delete(currentStep);
          return newSet;
        });
      }

      // Check if it's first visit or if enough time has passed since last break
      const currentTime = Date.now();
      const oneMinuteInMs = 5 * 60 * 1000; // 5 minute 
      const timeSinceLastBreak = currentTime - lastBreakTime;
      console.log('Time since last break:', timeSinceLastBreak / 1000, 'seconds');
      
      if (isFirstVisit || timeSinceLastBreak >= oneMinuteInMs) {
        console.log('Showing break modal');
        setShowBreakModal(true);
        setLastBreakTime(currentTime);
        setIsFirstVisit(false); // Set first visit to false after showing modal
      } else {
        console.log('Not showing break modal, continuing');
        window.scrollTo(0, 0);
        setCurrentStep(prev => prev + 1);
      }

      setHasUnsavedChanges(false);
      setIsSaving(false);
    } catch (error) {
      console.error("Error saving data:", error);
      setIsSaving(false);
    }
  };

    // Update handleContinueWithoutBreak to scroll first
    const handleContinueWithoutBreak = () => {
      setShowBreakModal(false);
      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
      // Scroll to top BEFORE changing the step
      window.scrollTo(0, 0);
      // Then change step
      setCurrentStep((prev) => prev + 1);
    };
  

  const handleTakeBreak = () => {
    setShowBreakModal(false);
    // Reset unsaved changes flag
    setHasUnsavedChanges(false);
    // Use React Router navigation instead of window.location
    navigate("/");
  };


  // Update handlePrevious to scroll first
  const handlePrevious = () => {
    // Scroll to top BEFORE changing the step
    window.scrollTo(0, 0);
    // Then change step
    setCurrentStep((prev) => prev - 1);
  };

  // Update handleFinalSubmit to properly validate without clearing errors immediately
  const handleFinalSubmit = async () => {
    try {
      // Prevent multiple submissions
      if (saveInProgress.current) {
        return;
      }
      
      saveInProgress.current = true;
      
      // Dismiss any existing toasts first
      toast.dismiss();
      
      // Get the latest field values from the ref for the validation check
      const latestValues = formValuesRef.current;
      const subject = latestValues[18]?.[0]?.[76];
      const content = latestValues[18]?.[0]?.[79];
      
      // Check if the fields are empty
      const subjectEmpty = !subject || subject.trim() === '';
      const contentEmpty = !content || content.trim() === '';
      
      // Show validation errors if fields are empty
      if (subjectEmpty || contentEmpty) {
        // Set validation errors
        setValidationErrors({
          ...(subjectEmpty && { '18_76_0': 'Please fill in the required field' }),
          ...(contentEmpty && { '18_79_0': 'Please fill in the required field' })
        });
        
        toast.error("Please fill out all required fields", {
          style: {
            background: '#F16745',
            color: '#fff',
            border: 'none'
          }
        });
        saveInProgress.current = false;
        return;
      }
      
      // Fields are valid, clear any existing validation errors
      setValidationErrors({});
      
      // Set loading state
      setIsSaving(true);
      
      // Cancel any pending debounced saves
      debouncedSaveStepData.cancel();
      
      // Proceed with saving using the latest values from the ref
      const saveSuccessful = await saveStepData(currentStep, formValuesRef.current);
      
      // Only proceed with completion if save was successful
      if (saveSuccessful) {
        if (isStepComplete(currentStep)) {
          setCompletedSteps((prev) => new Set([...prev, currentStep]));
        }
        // Show completion modal instead of alert
        setShowCompletionModal(true);
      } else {
        toast.error("There was an error submitting the form. Please try again.", {
          style: {
            background: '#F16745',
            color: '#fff',
            border: 'none'
          }
        });
      }
    } catch (error) {
      console.error("Error saving final data:", error);
      toast.error("There was an error submitting the form. Please try again.", {
        style: {
          background: '#F16745',
          color: '#fff',
          border: 'none'
        }
      });
    } finally {
      setIsSaving(false);
      saveInProgress.current = false;
    }
  };

  // Update the useEffect to use immediate scrolling instead of smooth behavior
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep, activeTab]);

  // Add a useEffect to fetch and store the user's name when the component mounts
  useEffect(() => {
    const fetchUserName = async () => {
      try {
        // Use the authService.getProfile method to fetch user data
        const response = await authService.getProfile();
        
        if (response.success && response.data.user) {
          setUserName(response.data.user.name);
          console.log("User name fetched:", response.data.user.name);
        }
      } catch (error) {
        console.error("Error fetching user name:", error);
      }
    };
    
    if (userId) {
      fetchUserName();
    }
  }, [userId]);

  // Function to get the current user's name
  const getCurrentUserName = () => {
    // Return the user's name from state, or a fallback
    return userName || `User ${userId}` || "User";
  };

  // Add this function near your other handler functions
  const handleFieldFocus = (field) => {
    // Track the last focused field
    setLastFocusedField(field);
  };

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending debounced saves
      debouncedSaveStepData.cancel();
      
      // Clear all timeouts
      Object.values(typingTimeouts).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      
      // Make sure we're not showing "unsaved changes" dialog when leaving
      setHasUnsavedChanges(false);
    };
  }, []);

  // Add this new state for tracking skipped fields
  const [skippedFields, setSkippedFields] = useState({});

  // Add this new function to handle skip status changes
  const handleSkipChange = async (sectionId, field, index, isSkipped) => {
    try {
      setIsSaving(true);
      
      // Prepare the value to save
      const currentValue = formValues[sectionId]?.[index]?.[field.id] || "";
      
      // Create payload for the API
      const payload = {
        form_id: Number(formData.id),
        step_id: Number(formData.form_form_steps[currentStep].id),
        user_id: Number(userId),
        values: [{
          field_id: Number(field.id),
          section: Number(sectionId),
          section_repeat_index: Number(index),
          value: currentValue,
          skip: isSkipped ? 1 : 0
        }]
      };

      const result = await formDataService.saveSkipStatus(payload);

      if (result.status === "success") {
        // Update local state to track skipped status
        setSkippedFields(prev => ({
          ...prev,
          [`${sectionId}_${field.id}_${index}`]: isSkipped
        }));

        // Update last save time
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        setHasUnsavedChanges(false);
      } else {
        throw new Error(result.message || 'Failed to update skip status');
      }
    } catch (error) {
      console.error("Error updating skip status:", error);
      toast.error("Failed to update skip status");
    } finally {
      setIsSaving(false);
    }
  };

  // Update useEffect to initialize skipped fields from API response
  useEffect(() => {
    if (!formData?.form_form_steps) return;

    const initializeFormData = async () => {
      setIsLoading(true);
      try {
        const responseJson = await formDataService.getUserData(userId);

        if (responseJson.success && responseJson.data.form_submissions?.[0]) {
          // Initialize skipped fields from submission values
          const newSkippedFields = {};
          responseJson.data.form_submissions[0].submission_values.forEach((submission) => {
            const key = `${submission.section}_${submission.field_id}_${submission.section_repeat_index || 0}`;
            if (submission.skip === 1) {
              newSkippedFields[key] = true;
            }
          });
          setSkippedFields(newSkippedFields);
          
          // ... rest of your existing initialization code ...
        }
      } catch (error) {
        console.error("Error initializing form data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFormData();
  }, [formData]);

  // Add useEffect to check for new user and make API call
  useEffect(() => {
    const handleNewUserCheck = async () => {
      try {
        const loginResponse = localStorage.getItem('loginResponse');
        if (!loginResponse) return;

        const parsedResponse = JSON.parse(loginResponse);
        if (parsedResponse.user?.is_new === 1) {
          // Only make the API call if we haven't already
          if (!newUserApiResponse) {
            const response = await userService.handleNewUser();
            console.log('New User API Response:', response);
            setNewUserApiResponse(response);
          }
        }
      } catch (error) {
        console.error('Error in new user check:', error);
        toast.error('Error checking new user status');
      }
    };

    if (isNewUser) {
      handleNewUserCheck();
    }
  }, [isNewUser, newUserApiResponse]); // Only run when isNewUser or newUserApiResponse changes

  // Render the component
  return (
    <div className="min-h-screen flex flex-col">
      <UnsavedChangesAlert hasUnsavedChanges={hasUnsavedChanges} />
      
      {/* Break Suggestion Modal */}
      {showBreakModal && (
        <BreakSuggestionModal
          isOpen={showBreakModal}
          onTakeBreak={handleTakeBreak}
          onContinue={handleContinueWithoutBreak}
        />
      )}

      {/* Form Completion Modal */}
      <FormCompletionModal
        isOpen={showCompletionModal}
        onNavigateToDashboard={() => navigate("/")}
      />

      {lastSaveTime && (
        <div className="fixed bottom-3 right-2 bg-[#F16745] rounded-lg px-4 py-3 flex items-center space-x-2 z-[9999]">
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span className="text-sm font-medium text-white">
            Last saved: {lastSaveTime}
          </span>
        </div>
      )}
      <Toaster
        toastOptions={{
          style: {
            marginTop: "60px",
          },
        }}
      />
      {isLoading ? (
        <div className="flex w-full items-center justify-center min-h-screen bg-[#F1EBDB]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F16745]"></div>
        </div>
      ) : (
        <div className="flex w-full flex-1">
          {/* Sidebar */}
          <FormSidebar 
            isOpen={isOpen}
            close={close}
            formName={formName}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            formData={formData}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            completedSteps={completedSteps}
            scrollToTop={scrollToTop}
          />

          {/* Main Content */}
          <main ref={mainContentRef} className="flex-1 lg:ml-80" style={{ backgroundColor: "#F1EBDB" }}>
            <div className="p-8 pt-16 md:pt-10">
              {activeTab === "video" ? (
                <div className="space-y-8">
                  {/* Video Section */}
                  <div className="max-w-3xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-[#F16745] mb-2">
                          Introduction
                        </h2>
                        {formData?.description && (
                          <p className="text-gray-600 mb-6">
                            {formData.description}
                          </p>
                        )}
                        
                        <div style={{padding:'56.25% 0 0 0', position:'relative'}}>
                          <iframe
                            src={VIDEO_URLS.INTRO_VIDEO}
                            style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}}
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            className="rounded-lg"
                            title="Introductory Video"
                          />
                        </div>

                        <div className="flex justify-end mt-6">
                          <button
                            onClick={() => {
                              setActiveTab("form");
                              setCurrentStep(0);
                              scrollToTop();
                            }}
                            className="px-6 py-2.5 bg-[#F16745] text-sm font-medium rounded-lg text-white hover:bg-[#F16745] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
                          >
                            Lets Begin →
                          </button>
                        </div>
                      </div>
                        {/* New User Introductory Text - Moved outside video container */}
                    {isNewUser && (
                      
                      <div className="mt-8 pt-8">
                        <div className="prose prose-lg text-gray-600">
                          <p>Each year, approximately 600,000 people pass away in the UK. As each generation goes by, the memory of those people begins to fade. They don&apos;t get remembered for who they were when they were young or what their dreams and ambitions were. They don&apos;t get to tell their story in their own words.</p>
                          
                          <p>PostScrypt allows you to provide both practical and sentimental guidance to those you leave behind.</p>
                          
                          <p>Whilst any information you provide in PostScrypt is not legally binding, the practical information you leave can support your loved ones and your Executors when they are administering your estate, ensuring that your affairs are dealt with promptly and in accordance with your wishes.</p>
                          
                          <p>PostScrypt also gives you the opportunity to detail your emotional wishes, including who you would like to leave your sentimental and personal belongings to and how you&apos;d like to be remembered at your funeral.</p>
                          
                          <p>We understand that the PostScrypt process can be emotional, but by completing each section, you can completely change the experience of family and friends when grieving the loss of their loved one. PostScrypt can and will be treasured by future generations, far beyond the distribution of your financial legacy.</p>
                        </div>
                      </div>
                    )}
                    </div>

                  
                  </div>

                  {/* Questionnaire Section */}
                  <div className="max-w-3xl mx-auto">
                    <InitialQuestionnaireDisplay 
                      setLastSaveTime={setLastSaveTime}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      debouncedSaveStepData={debouncedSaveStepData}
                      onContinue={() => {
                        setActiveTab("form");
                        setCurrentStep(0);
                        scrollToTop();
                      }}
                      onBreak={() => {
                        navigate("/");
                      }}
                      showBreakModal={showBreakModal}
                      setShowBreakModal={setShowBreakModal}
                      lastBreakTime={lastBreakTime}
                      setLastBreakTime={setLastBreakTime}
                      isSaving={isSaving}
                      setIsSaving={setIsSaving}
                      hasUnsavedChanges={hasUnsavedChanges}
                    />
                  </div>
                </div>
              ) : activeTab === "contacts" ? (
                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 mb-28">
                  <h2 className="text-2xl font-bold text-[#F16745] mb-4">
                    My PostScrypt Contacts
                  </h2>
                  <p className="text-gray-600 mb-2">
                    Use this section to add the person or people you want us to send the information contained in PostScrypt to when you pass away. We Recommend that this is your Executor(s).
                  </p>
                  <p className="text-gray-600 mb-2">
                    Please note that PostScrypt is not a legally binding document. If you want to change your Executor, your Will must be changed accordingly. Any instructions within PostScrypt that you wish to make legally binding should be done so via your Will provider.
                  </p>
                  <p className="text-gray-600 mb-2">
                    For context, the client can add one or more contacts. When the client is marked as deceased on PostScrypt, any contacts provided will receive an email with a link that will enable them to view the client&apos;s PostScrypt account.
                  </p>
                  <p className="text-gray-600 mb-6">
                    By completing this information, you are consenting to sharing all information contained in your PostScrypt account with the contact details you have provided.
                  </p>
                  <PostScryptContacts onClose={() => setActiveTab("form")} />
                </div>
              ) : activeTab === "will" ? (
                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 mb-48">
                  <h2 className="text-2xl font-bold text-[#F16745] mb-4">
                    Changing Your Will
                  </h2>
                  <p className="text-gray-600 mb-2">
                    PostScrypt is not a legally binding document. Your Will overrides any information or instructions you provide with PostScrypt.
                  </p>
                  <p className="text-gray-600 mb-6">
                    To make a change to your Will, please contact Right Legal Group.
                  </p>
                  <ChangingWill />
                </div>
              ) : (
                <div>
                  <div className="max-w-3xl mb-6 mx-auto bg-white rounded-xl shadow-sm p-8 ">
                  <h2 className="text-2xl font-bold text-[#F16745] mb-4">
                    {formData.form_form_steps[currentStep].title}
                  </h2>
                  {formData.form_form_steps[currentStep].description && (
                    <div className="text-gray-600">
                    {formData.form_form_steps[currentStep].description.split('/n').map((line, index) => (
                      <React.Fragment key={index}>
                        <p className={`${index > 0 ? 'mt-1' : ''}`}>{line}</p>
                      </React.Fragment>
                    ))}
                  </div>
                  )}
                  </div>
                 
                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8">
                 

                  {formData.form_form_steps[
                    currentStep
                  ].form_step_form_sections.map((section) => 
                      // Only render the section if it's not hidden
                    !section.hidden && (
                      <div key={section.id} className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {section.name}
                            </h3>
                            {section.description && (
                              <p className="text-gray-600 text-sm">
                                {section.description}
                              </p>
                            )}
                          </div>
                          {section.is_repeatable === "Yes" && (
                            <button
                              type="button"
                              onClick={() => addRepeatableSection(section.id)}
                              className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#F16745] hover:bg-[#F16745] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745]"
                            >
                              Add More
                            </button>
                          )}
                        </div>
                        {(formValues[section.id] || []).map((rowInstance, index) => (
                          <div 
                            key={index} 
                            data-section-id={section.id}
                            className={`rounded-lg mb-6 ${
                              newlyAddedSection && 
                              newlyAddedSection.sectionId === section.id && 
                              newlyAddedSection.index === index
                                ? 'outline-4 outline outline-[#F1EBDB] animate-pulse-border'
                                : ''
                            }`}
                          >
                            {/* Add remove button at the top if it's not a default row */}
                            {section.is_repeatable === "Yes" && index >= (parseInt(section.default_repeat_count) || 1) && (
                              <>
                                <div className="mb-4 flex justify-start">
                                  <button
                                    type="button"
                                    onClick={() => removeRepeatableSection(section.id, index-1)}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <hr className="border-t-4 border-dotted border-[#F16745] my-4 w-[85%]" />
                              </>
                            )}
                            
                            <div className="grid grid-cols-1 gap-6">
                              {section.form_step_form_fields.map(
                                (field) => {
                                  // Special handling only for section 4
                                  const isHidden = section.id === 4 ? 
                                    (field.id === 17 && formValues[section.id]?.[0]?.[16] !== 'yes') ||
                                    (field.id === 20 && formValues[section.id]?.[0]?.[19] !== 'yes') ||
                                    field.hidden
                                    : field.hidden;
                                  
                                  // Special handling for section 18
                                  let fieldValue = rowInstance[field.id] || "";
                                  
                                  // For section 18, directly set values for fields 77 and 78
                                  if (section.id === 18) {
                                    if (field.id === 77) {
                                      fieldValue = getCurrentUserName();
                                      console.log("Rendering field 77 with value:", fieldValue);
                                    } else if (field.id === 78) {
                                      fieldValue = new Date().toISOString().split('T')[0];
                                      console.log("Rendering field 78 with value:", fieldValue);
                                    }
                                  }
                                  
                                  return !isHidden && (
                                    <div
                                      key={field.id}
                                      className="space-y-2"
                                    >
                                      {field.field_type !== "heading" && (
                                        <div className="flex items-center space-x-2 max-w-2xl">
                                          <label className="block font-medium text-gray-700 w-auto">
                                            {field.name}
                                            {field.required === 1 && (
                                              <span className="text-red-500 ml-1">
                                                *
                                              </span>
                                            )}
                                          </label>
                                          {field.example_answer && (
                                            null
                                          )}
                                        </div>
                                      )}

                                      {/* Render input based on field type */}
                                      {field.field_type === "text" && (
                                        <SkippableInput
                                          type="text"
                                          value={fieldValue}
                                          onChange={(e) => handleChange(section.id, field, e.target.value, index)}
                                          onBlur={(e) => handleFieldBlur({...field, sectionId: section.id}, e.target.value)}
                                          onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                          field={field}
                                          sectionId={section.id}
                                          index={index}
                                          isSkipped={skippedFields[`${section.id}_${field.id}_${index}`]}
                                          onSkipChange={(isSkipped) => handleSkipChange(section.id, field, index, isSkipped)}
                                          validationError={validationErrors[`${section.id}_${field.id}_${index}`]}
                                        />
                                      )}

                                      {field.field_type === "date" && (
                                        <SkippableInput
                                          type="date"
                                          value={fieldValue}
                                          onChange={(e) => handleChange(section.id, field, e.target.value, index)}
                                          onBlur={(e) => handleFieldBlur({...field, sectionId: section.id}, e.target.value)}
                                          onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                          field={field}
                                          sectionId={section.id}
                                          index={index}
                                          isSkipped={skippedFields[`${section.id}_${field.id}_${index}`]}
                                          onSkipChange={(isSkipped) => handleSkipChange(section.id, field, index, isSkipped)}
                                          validationError={validationErrors[`${section.id}_${field.id}_${index}`]}
                                        />
                                      )}

                                      {field.field_type === "checkbox" && (
                                        <div className="flex items-center py-2">
                                          <input
                                            id={"field_" + field.id}
                                            data-field={field}
                                            type="checkbox"
                                            className="h-5 w-5 rounded border-gray-300 text-[#F16745] focus:ring-[#F16745] transition duration-150"
                                            checked={
                                              rowInstance[field.id] === "1"
                                            }
                                            onChange={(e) => {
                                              handleChange(
                                                section.id,
                                                field,
                                                e.target.checked ? "1" : "0",
                                                index
                                              );
                                              handleFieldBlur(
                                                field,
                                                e.target.checked ? "1" : "0"
                                              );
                                            }}
                                            onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                          />
                                          <span className="ml-3 text-sm text-gray-600">
                                            Select this option
                                          </span>
                                        </div>
                                      )}

                                      {field.field_type === "textarea" && (
                                        <SkippableInput
                                          type="textarea"
                                          value={rowInstance[field.id] || ""}
                                          onChange={(e) => handleChange(section.id, field, e.target.value, index)}
                                          onBlur={(e) => handleFieldBlur({...field, sectionId: section.id}, e.target.value)}
                                          onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                          rows={field.rows || 4}
                                          field={field}
                                          sectionId={section.id}
                                          index={index}
                                          isSkipped={skippedFields[`${section.id}_${field.id}_${index}`]}
                                          onSkipChange={(isSkipped) => handleSkipChange(section.id, field, index, isSkipped)}
                                          validationError={validationErrors[`${section.id}_${field.id}_${index}`]}
                                        />
                                      )}

                                      {field.field_type === "number" && (
                                        <SkippableInput
                                          type="number"
                                          value={rowInstance[field.id] || ""}
                                          onChange={(e) => handleChange(section.id, field, e.target.value, index)}
                                          onBlur={(e) => handleFieldBlur({...field, sectionId: section.id}, e.target.value)}
                                          onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                          field={field}
                                          sectionId={section.id}
                                          index={index}
                                          isSkipped={skippedFields[`${section.id}_${field.id}_${index}`]}
                                          onSkipChange={(isSkipped) => handleSkipChange(section.id, field, index, isSkipped)}
                                          validationError={validationErrors[`${section.id}_${field.id}_${index}`]}
                                        />
                                      )}

                                      {field.field_type === "file" && (
                                        <div className="relative">
                                          <FileUploadDropzone
                                            section={section}
                                            field={{
                                              ...field,
                                              formId: formData?.id,
                                              userId: userId
                                            }}
                                            index={index}
                                            value={rowInstance[field.id]}
                                            onChange={(fileData) => handleChange(section.id, field, fileData, index)}
                                            onBlur={(e) => handleFieldBlur(field, rowInstance[field.id])}
                                            onFocus={() => handleFieldFocus({...field, sectionId: section.id})}
                                            isFormLoading={isLoading}
                                          />
                                        </div>
                                      )}

                                      {field.field_type === "heading" && (
                                        <h3 className="text-lg font-semibold text-gray-900 mt-2 ">
                                          {field.label || field.name}
                                        </h3>
                                      )}

                                      {field.field_type === "radio" && (
                                        <div className="space-y-2">
                                          {Array.isArray(
                                            JSON.parse(field.options)
                                          ) &&
                                            JSON.parse(field.options).map(
                                              (option) => (
                                                <div
                                                  key={option.value}
                                                  className="flex items-center"
                                                >
                                                  <input
                                                    type="radio"
                                                    className="h-4 w-4 border-gray-300 text-[#F16745] focus:ring-[#F16745]"
                                                    id={`${field.name}-${option.value}`}
                                                    value={option.value}
                                                    checked={
                                                      rowInstance[
                                                        field.id
                                                      ] === option.value
                                                    }
                                                    onChange={(e) =>
                                                      handleChange(
                                                        section.id,
                                                        field,
                                                        e.target.value,
                                                        index
                                                      )
                                                    }
                                                  />
                                                  <label
                                                    htmlFor={`${field.name}-${option.value}`}
                                                    className="ml-3 block  text-gray-700"
                                                  >
                                                    {option.label}
                                                  </label>
                                                </div>
                                              )
                                            )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}

                  {/* Navigation Buttons */}
                  <div className="mt-8 flex justify-between  pt-6">
                    {currentStep > 0 && (
                      <button
                        type="button"
                        onClick={handlePrevious}
                        // focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745]
                        className="px-6 py-2.5 border border-[#F16745] text-sm font-medium rounded-lg text-[#F16745] bg-white hover:bg-[#F1EBDB]  transition duration-150"
                      >
                        ← Previous
                      </button>
                    )}

                    <div className="flex-1"></div>

                    {currentStep < formData.form_form_steps.length - 1 ? (
                      <>
                        {/* <button
                          type="button"
                          onClick={handleNext}
                          className="px-6 py-2.5 bg-[#F16745] text-sm font-medium rounded-lg text-white hover:bg-[#e05635] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
                        >
                          Next →
                        </button> */}
                        <button
                          type="button"
                          onClick={handleSaveAndNext}
                          className="ml-2 bg-[#F16745] px-6 py-2.5  text-sm font-medium rounded-lg text-white hover:bg-[#ee623f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <span className="flex items-center">
                              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                              Saving...
                            </span>
                          ) : (
                            "Save & Next →"
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={handleFinalSubmit}
                        className="px-6 py-2.5 bg-green-600 text-sm font-medium rounded-lg text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150"
                      >
                        Save
                      </button>
                    )}
                  </div>
                </div>
                </div>
              )}
            </div>
            <div className="lg:ml-[70px]">
         <Footer/>
      </div>  
          </main>
        </div>
      )}
    </div>
  );
};

export default DynamicForm;
